import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commissionOutputApi } from "../../../../modules/commissions/outputs/api";
import {
  CalculatedCommissionsFilterExportRequest,
  CalculatedCommissionsFilterPageRequest,
  CommissionsOutputFilterExportRequest,
  CommissionsOutputFilterPageRequest,
  CreateUpdateCommissionsOutput,
} from "../../../../modules/commissions/outputs/types";
import { SpecialCommissionsFilterPageRequest } from "../../../../modules/commissions/special/types";
import {
  EntityObject,
  OptimisticLockVersionList,
  PageRequest,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../types";
import { COMMISSIONS_BATCH_QUERY_KEYS, COMMISSIONS_OUTPUT_QUERY_KEYS } from "./queryKeys";

export const useFilterCommissionsOutputs = (payload: CommissionsOutputFilterPageRequest) => {
  return useQuery({
    queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs(payload),
    queryFn: async () => {
      const { data } = await commissionOutputApi.filterCommissionsOutputs(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useDownloadCommissionsOutputsExport = () => {
  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.downloadCommissionsOutputsExport(),
    mutationFn: async (payload: CommissionsOutputFilterExportRequest) => {
      const response = await commissionOutputApi.downloadCommissionsOutputsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useFilterAgentCommissionsOutputs = (payload: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs(payload),
    queryFn: async () => {
      const { data } = await commissionOutputApi.filterAgentCommissionsOutputs(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateAgentCommissionsOutput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.createAgentCommissionsOutput(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCommissionsOutput>) => {
      const { data } = await commissionOutputApi.createAgentCommissionsOutput(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs() });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
    },
  });
};

export const useUpdateAgentCommissionsOutput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.updateAgentCommissionsOutput(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateCommissionsOutput>) => {
      const { data } = await commissionOutputApi.updateAgentCommissionsOutput(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs() });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
    },
  });
};

export const useToggleAgentCommissionsOutput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.togglePaidAgentCommissionsOutput(),
    mutationFn: async (payload: OptimisticLockVersionList) => {
      const { data } = await commissionOutputApi.togglePaidCommissionsOutput(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs() });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
    },
  });
};

export const useDeleteAgentCommissionsOutput = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.deleteAgentCommissionsOutput(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionOutputApi.deleteAgentCommissionsOutput(payload);
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterAgentCommissionsOutputs() });
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCommissionsOutputs() });
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_BATCH_QUERY_KEYS.filterCommissionsBatchOutputs(),
      });
    },
  });
};

export const useFilterCalculatedCommissionsForAgentOutputs = (
  payload: TwoLevelEntityObject<CalculatedCommissionsFilterPageRequest>,
) => {
  return useQuery({
    queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterCalculatedCommissionsForAgentOutputs(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await commissionOutputApi.filterCalculatedCommissionsForAgentOutputs(payload);

      return data;
    },
    enabled: !!payload.id1.length && !!payload.id2.length,
    placeholderData: keepPreviousData,
  });
};

export const useDownloadCalculatedCommissionsExportForAgentOutputs = () => {
  return useMutation({
    mutationKey: COMMISSIONS_OUTPUT_QUERY_KEYS.downloadCalculatedCommissionsExportForAgentOutputs(),
    mutationFn: async (payload: TwoLevelEntityObject<CalculatedCommissionsFilterExportRequest>) => {
      const response = await commissionOutputApi.downloadCalculatedCommissionsExportForAgentOutputs(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useFilterSpecialCommissionsForAgentOutputs = (
  payload: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>,
) => {
  return useQuery({
    queryKey: COMMISSIONS_OUTPUT_QUERY_KEYS.filterSpecialCommissionsForAgentOutputs(payload),
    queryFn: async () => {
      const { data } = await commissionOutputApi.filterSpecialCommissionsForAgentOutputs(payload);

      return data;
    },
    enabled: !!payload.id1.length && !!payload.id2.length,
  });
};
